import React, { useState, useEffect } from 'react';
import { FolderOpen, Plus, Trash2, X, Upload, Loader2, Music } from 'lucide-react';
import { listProjects, deleteProject } from '@/api/projects';

export const ProjectsModal = ({ isOpen, onClose, onLoadProject, onNewProject, currentProjectId }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await listProjects();
        setProjects(data);
      } catch (e) {
        console.warn('Failed to load projects:', e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen]);

  const handleDelete = async (pid) => {
    try {
      await deleteProject(pid);
      setProjects((prev) => prev.filter((p) => p.id !== pid));
    } catch (e) {
      console.warn('Delete failed:', e.message);
    }
  };

  if (!isOpen) return null;

  return (
    <div data-testid="projects-modal" className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="flex flex-col" style={{ width: 480, maxHeight: '80vh', background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-2">
            <FolderOpen size={18} style={{ color: 'var(--green)' }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>My Projects</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-dim)' }}>({projects.length}/15)</span>
          </div>
          <button data-testid="close-projects" onClick={onClose} style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg-elevated)', color: 'var(--text-dim)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-dim)' }} />
            </div>
          )}

          {!loading && projects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Music size={28} style={{ color: 'var(--text-dim)' }} />
              <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>No saved projects yet</span>
              <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Click Save to save your current beat</span>
            </div>
          )}

          {!loading && projects.map((project) => (
            <div
              key={project.id}
              data-testid={`project-item-${project.id}`}
              className="flex items-center gap-3 px-4 py-3"
              style={{
                background: project.id === currentProjectId ? 'var(--bg)' : 'var(--bg-elevated)',
                borderRadius: 10,
                border: `1px solid ${project.id === currentProjectId ? 'var(--green)' : 'var(--border)'}`,
              }}
            >
              <div className="flex flex-col flex-1 min-w-0">
                <span className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{project.name}</span>
                <div className="flex items-center gap-2">
                  <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{project.bpm} BPM</span>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{project.total_bars} bars</span>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                    {new Date(project.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              {project.id !== currentProjectId && (
                <button
                  data-testid={`load-project-${project.id}`}
                  onClick={() => { onLoadProject?.(project.id); onClose?.(); }}
                  style={{ height: 28, borderRadius: 22, padding: '0 14px', fontSize: 11, fontWeight: 600, background: 'var(--green)', color: 'var(--bg)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Upload size={11} /> Load
                </button>
              )}
              {project.id === currentProjectId && (
                <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--green)', padding: '4px 10px', borderRadius: 22, background: 'var(--green)15' }}>Current</span>
              )}
              <button
                data-testid={`delete-project-${project.id}`}
                onClick={() => handleDelete(project.id)}
                className="transition-opacity opacity-40 hover:opacity-100"
                style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--bg)', color: 'var(--pink)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <button
            data-testid="new-project-btn"
            onClick={() => { onNewProject?.(); onClose?.(); }}
            className="flex items-center justify-center gap-2 w-full"
            style={{ height: 38, borderRadius: 22, fontSize: 12, fontWeight: 600, background: 'var(--green)', color: 'var(--bg)', border: 'none', cursor: 'pointer' }}
          >
            <Plus size={14} /> New Project
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectsModal;
